// src/components/co-ownership/CoOwnershipShowcase.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';

// UI and Data Components
import { FundingAgentCard, FundingAgentCardData } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { loadFundingAgents } from '@dopomogai/data';

interface CoOwnershipShowcaseProps {
  onAction: (actionType: string, payload?: any) => void;
}

export const CoOwnershipShowcase: React.FC<CoOwnershipShowcaseProps> = ({ onAction }) => {
  const { t } = useTranslation('co_ownership');
  const showcaseData = t('showcase', { returnObjects: true }) as any;

  // --- State for Data and Pagination ---
  const [allAgents, setAllAgents] = useState<FundingAgentCardData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // Fetch agent data when the component mounts
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const data = await loadFundingAgents();
      setAllAgents(data);
      setIsLoading(false);
    };
    fetchData();
  }, []);
  
  // --- Pagination Logic ---
  const totalPages = Math.ceil(allAgents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAgents = allAgents.slice(startIndex, endIndex);

  // --- UI and Loading State ---
  if (isLoading) {
    // ... same loading state as before ...
  }

  return (
    <section id="co-ownership-showcase" className="bg-white py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true, amount: 0.3 }} 
          transition={{ duration: 0.5 }} 
          className="text-center max-w-3xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{showcaseData.headline}</h2>
          <p className="mt-4 text-lg text-slate-600">{showcaseData.subheadline}</p>
        </motion.div>
        
        {allAgents.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              {currentAgents.map((agent, index) => (
                <FundingAgentCard
                  key={agent.id}
                  data={agent}
                  onAction={onAction}
                  index={index}
                />
              ))}
            </div>

            {/* --- NEW: PAGINATION CONTROLS --- */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-12">
                <Button
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={currentPage === 1}
                  variant="outline"
                  icon={<ArrowLeft size={16} />}
                  ariaLabel="Previous Page"
                />
                <span className="font-medium text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  icon={<ArrowRight size={16} />}
                  ariaLabel="Next Page"
                />
              </div>
            )}
            
            <div className="mt-16 text-center">
              <Button
                text={t('showcase.ctaViewAllAgents')}
                variant="secondary"
                size="lg"
                onClick={() => onAction('NAVIGATE_TO_PAGE', { path: '/agents' })}
              />
            </div>
          </>
        ) : (
          <div className="text-center py-20 px-6 mt-12 col-span-full bg-slate-50 border-2 border-black rounded-xl">
            <h3 className="text-2xl font-bold text-slate-800">{showcaseData.emptyState}</h3>
            <div className="mt-6">
              <Button 
                text={t('showcase.ctaWaitlist')}
                onClick={() => onAction('SHOW_MODAL', { modalType: 'EARLY_ACCESS_SIGNUP' })}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

